/**
 * Strong Bold Extension for tiptap-markdown
 *
 * **텍스트**뒤에 어떤 문자가 와도 정상적으로 볼드 처리되도록
 * markdown-it에 커스텀 파서 룰을 등록합니다.
 *
 * GFM 마크다운은 **bold** 뒤에 공백 없이 문자가 붙으면 볼드가 닫히지 않는 문제가 있습니다.
 * 이 extension은 **텍스트**문자 패턴에서도 볼드가 정상 종료되도록 처리합니다.
 */

import { Extension } from '@tiptap/core';
import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core';
import type Token from 'markdown-it/lib/token';

/**
 * Strong Bold Extension
 * tiptap-markdown의 markdown-it 인스턴스에 bold 플러그인을 등록
 *
 * markdown-it의 core ruler에 전처리 규칙을 추가하여
 * **텍스트**문자 패턴을 HTML <strong> 태그로 변환합니다.
 */
export const StrongBold = Extension.create({
	name: 'strongBold',

	addStorage() {
		return {
			markdown: {
				parse: {
					setup(md: MarkdownIt) {
						// bold 패턴 전처리 규칙 추가
						// markdown-it의 inline 파싱 전에 **텍스트**문자 패턴을 처리
						md.core.ruler.before('inline', 'strong_preprocess', (state: StateCore) => {
							state.tokens.forEach((token: Token) => {
								if (token.type === 'inline' && token.content) {
									// **텍스트** 뒤에 문자가 바로 붙어있는 경우 처리
									token.content = processStrongBold(token.content);
								}
							});
						});
					},
				},
			},
		};
	},
});

/**
 * Bold 패턴 처리
 * **텍스트**뒤에 공백 없이 어떤 문자가 붙어도 볼드가 정상 종료되도록 처리
 *
 * @param content - 원본 마크다운 문자열
 * @returns 처리된 문자열 (HTML strong 태그 포함)
 */
const processStrongBold = (content: string): string => {
	// 코드 스팬(`...`)을 기준으로 분리하여 코드 블록 내부는 변환하지 않음
	const segments = content.split(/(`[^`]*`)/g);

	return segments
		.map((segment) => {
			// 백틱으로 감싸진 코드 스팬은 그대로 유지
			if (segment.startsWith('`') && segment.endsWith('`')) {
				return segment;
			}
			// 코드 스팬이 아닌 부분만 볼드 처리
			// .+? (non-greedy)로 내부에 * 문자가 있어도 처리 가능
			// GFM의 단어 경계 규칙을 우회하여 어떤 문자가 뒤에 와도 볼드 종료
			// eslint-disable-next-line no-useless-escape
			return segment.replace(/\*\*(.+?)\*\*(?=[^\s*\[\]]|$)/g, '<strong>$1</strong>');
		})
		.join('');
};
